import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { HiOutlineMenuAlt4, HiOutlineX } from "react-icons/hi";
import { SITE, NAV, SERVICES } from "../data/site";

function hashFromScroll() {
  const header = document.querySelector(".header");
  const offset = (header?.getBoundingClientRect().bottom ?? 72) + 12;
  let current = "";
  for (const item of NAV) {
    const id = item.to.startsWith("/#") ? item.to.slice(2) : "";
    if (!id) continue;
    const el = document.getElementById(id);
    if (el && el.getBoundingClientRect().top <= offset) current = `#${id}`;
  }
  return current;
}

function sectionFromNav(to) {
  if (to.startsWith("/#")) return `#${to.slice(2)}`;
  if (to === "/") return "";
  return null;
}

export default function Header() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pinned = useRef(null);
  const syncRef = useRef(() => {});
  const isHome = pathname === "/";
  const overlay = isHome || pathname === "/reinigung" || pathname === "/kontakt";
  const lenis = useLenis();

  useLenis(() => {
    syncRef.current();
  });

  const isNavActive = (item) => {
    if (item.to.startsWith("/#")) {
      const id = item.to.slice(2);
      if (id === "leistungen" && pathname.startsWith("/leistungen/")) return true;
      return pathname === "/" && hash === `#${id}`;
    }
    if (item.to === "/") {
      return pathname === "/" && !hash;
    }
    return pathname === item.to;
  };

  const pinSection = (to) => {
    const section = sectionFromNav(to);
    if (section == null) return;
    pinned.current = section;
  };

  useEffect(() => {
    setOpen(false);
    if (pathname !== "/") {
      pinned.current = null;
      return;
    }
    if (window.location.hash) pinned.current = window.location.hash;
  }, [pathname]);

  useEffect(() => {
    if (pathname !== "/") return;

    const sync = () => {
      const next = hashFromScroll();
      if (pinned.current != null) {
        if (next === pinned.current) pinned.current = null;
        else return;
      }
      if (window.location.hash === next) return;
      navigate(
        { pathname: "/", hash: next },
        { replace: true, state: { scrollSpy: true } }
      );
    };
    syncRef.current = sync;

    const releasePin = () => {
      pinned.current = null;
    };

    sync();
    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    window.addEventListener("wheel", releasePin, { passive: true });
    window.addEventListener("touchmove", releasePin, { passive: true });
    return () => {
      syncRef.current = () => {};
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      window.removeEventListener("wheel", releasePin);
      window.removeEventListener("touchmove", releasePin);
    };
  }, [pathname, lenis, navigate]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lenis]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const classes = [
    "header",
    overlay ? "is-home" : "is-solid",
    scrolled ? "is-scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header className={classes}>
        <div className="header-inner">
          <Link to="/" className="brand" aria-label={`${SITE.name} – Startseite`}>
            <img src="/logo.avif" width="52" height="32" alt="" />
            <span className="brand-text">
              <strong>CCS</strong>
              <span>Gebäudemanagement</span>
            </span>
          </Link>
          <ul className="nav-desktop">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={() => (isNavActive(item) ? "active" : "")}
                  aria-current={isNavActive(item) ? "page" : undefined}
                  end={item.to === "/"}
                  onClick={() => pinSection(item.to)}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <a className="btn btn-primary header-cta" href={`tel:${SITE.phoneTel}`}>
            {SITE.phoneDisplay}
          </a>
          <button
            type="button"
            className="menu-btn"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            onClick={() => setOpen(true)}
          >
            <HiOutlineMenuAlt4 size={22} />
          </button>
        </div>
      </header>
      <div
        id="mobile-nav"
        className={`nav-sheet ${open ? "is-open" : ""}`}
        inert={!open}
        aria-hidden={!open}
      >
        <div className="nav-sheet-top">
          <Link to="/" className="brand" onClick={() => setOpen(false)}>
            <img src="/logo.avif" width="52" height="32" alt="" />
            <span className="brand-text">
              <strong>CCS</strong>
              <span>Gebäudemanagement</span>
            </span>
          </Link>
          <button
            type="button"
            className="menu-btn"
            aria-label="Menü schließen"
            onClick={() => setOpen(false)}
          >
            <HiOutlineX size={22} />
          </button>
        </div>
        <nav>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={isNavActive(item) ? "active" : ""}
              aria-current={isNavActive(item) ? "page" : undefined}
              onClick={() => {
                pinSection(item.to);
                setOpen(false);
              }}
            >
              {item.label}
            </Link>
          ))}
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/leistungen/${s.slug}`}
              className={pathname === `/leistungen/${s.slug}` ? "active" : ""}
              aria-current={pathname === `/leistungen/${s.slug}` ? "page" : undefined}
              onClick={() => setOpen(false)}
              style={{ fontSize: "1.35rem", opacity: 0.8 }}
            >
              {s.title}
            </Link>
          ))}
        </nav>
        <div className="nav-sheet-foot">
          <a className="btn btn-primary" href={`tel:${SITE.phoneTel}`}>
            Anrufen · {SITE.phoneDisplay}
          </a>
          <a className="btn btn-ghost" href={`mailto:${SITE.email}`}>
            {SITE.email}
          </a>
        </div>
      </div>
    </>
  );
}
